import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Lock, Rocket, Upload, FileSignature, CheckCircle2, Circle, Clock,
  FileText, Paperclip, Send, StickyNote, Mail, MessageSquare, ShieldCheck, Rss, Undo2,
  Pencil, Download, Eye, History, ExternalLink, RefreshCw, Wrench, Trash2, BellRing, X, Users, Archive, ArchiveRestore,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import api, { apiError, fileUrl, openPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReadinessCard } from "@/components/ReadinessCard";
import { STAGES, fmtDate, expiryBadge, ROLE_LABELS, ROLE_COLORS } from "@/lib/helpers";
import ProgressRing from "@/components/ProgressRing";
import SignaturePad from "@/components/SignaturePad";
import { FinanceTab, LegalTab, IssueExtras, ContractorPicker } from "@/components/PropertyFinance";
import { BookingsTab } from "@/components/Bookings";
import { PublicReportCard } from "@/components/PublicReportCard";
import { CleaningCard } from "@/components/CleaningCard";
import { OnboardingFields } from "@/components/OnboardingForm";
import { usePublicSettings } from "@/context/PublicSettingsContext";
import Expenses from "@/pages/Expenses";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const STATUS_ICON = { complete: CheckCircle2, submitted: Send, in_progress: Clock, pending: Circle, not_needed: Circle };
const STATUS_COLOR = { complete: "text-[#2B4C3F]", submitted: "text-[#3a4a6b]", in_progress: "text-[#D98324]", pending: "text-muted-foreground", not_needed: "text-muted-foreground/40" };
const STATUS_LABEL = { complete: "Verified", submitted: "Submitted", in_progress: "In Progress", pending: "Required", not_needed: "Not Needed" };

function ManualAssignPopover({ itemId, onAdd }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const submit = () => { if (!name.trim()) return; onAdd(name.trim()); setName(""); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setName(""); }}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" className="h-7 text-[11px]" data-testid={`assign-manual-${itemId}`}>+ Name</Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2" align="end">
        <div className="flex gap-1.5">
          <Input autoFocus data-testid={`manual-name-input-${itemId}`} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Type a name…" className="h-8 text-xs" />
          <Button size="sm" className="h-8" data-testid={`manual-name-add-${itemId}`} onClick={submit} disabled={!name.trim()}>Add</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "checklist");
  const [p, setP] = useState(null);
  const [uploadItem, setUploadItem] = useState(null);
  const [signItem, setSignItem] = useState(null);
  const [audit, setAudit] = useState([]);
  const [pushes, setPushes] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const isStaff = ["admin", "operations", "compliance"].includes(user.role);
  useEffect(() => { if (isStaff) api.get("/users").then((r) => setStaffList(r.data.filter((u) => ["admin", "operations", "compliance"].includes(u.role)))).catch(() => {}); }, [isStaff]);

  const load = useCallback(() => {
    api.get(`/properties/${id}`).then((r) => setP(r.data));
    api.get(`/properties/${id}/audit`).then((r) => setAudit(r.data)).catch(() => {});
    api.get(`/properties/${id}/portal-pushes`).then((r) => setPushes(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!p) return <div className="overline text-muted-foreground">Loading…</div>;

  const canManage = ["admin", "operations"].includes(user.role);
  const complete = p.progress.pct === 100;
  const hasExpired = (p.documents || []).some((d) => !d.archived && d.expiry_status === "expired");

  const canEditItem = (item) =>
    isStaff || user.role === item.owner_role;

  const setStatus = async (item, status, rejection_note) => {
    try {
      const { data } = await api.patch(`/properties/${id}/checklist/${item.id}`, { status, rejection_note });
      setP((prev) => ({ ...prev, ...data, documents: prev.documents, communications: prev.communications, signatures: prev.signatures }));
      load();
      toast.success("Task updated");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const patchItem = async (item, body) => { try { await api.patch(`/properties/${id}/checklist/${item.id}`, body); load(); } catch (e) { toast.error(apiError(e)); } };
  const toggleAssignee = (item, uid) => { const cur = item.assignees || []; patchItem(item, { assignees: cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid] }); };
  const addManualAssignee = (item, name) => { const v = (name || "").trim(); if (!v) return; const cur = item.manual_assignees || []; if (cur.includes(v)) return; patchItem(item, { manual_assignees: [...cur, v] }); };
  const removeManualAssignee = (item, name) => patchItem(item, { manual_assignees: (item.manual_assignees || []).filter((n) => n !== name) });
  const delCustom = async (item) => { if (!window.confirm(`Delete custom task "${item.name}"?`)) return; try { await api.delete(`/properties/${id}/checklist/${item.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); } };
  const addCustom = async (stage) => { const name = window.prompt(`New task name for "${stage}":`); if (!name) return; try { await api.post(`/properties/${id}/checklist`, { name, stage }); toast.success("Task added"); load(); } catch (e) { toast.error(apiError(e)); } };
  const staffName = (uid) => (staffList.find((s) => s.id === uid) || {}).name || "Staff";

  const goLive = async () => {
    try {
      await api.post(`/properties/${id}/go-live`);
      toast.success("Property is now LIVE 🎉");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const unpublish = async () => {
    await api.post(`/properties/${id}/unpublish`);
    load();
  };

  const viewFeed = async () => {
    try {
      const { data } = await api.get(`/properties/${id}/feed`);
      toast.success("Syndication feed generated", { description: `${data.title} → market-ready payload ready to push.` });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <button data-testid="back-button" onClick={() => nav("/properties")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-4 w-4" /> All properties
      </button>

      {/* Header with gate */}
      <div className="bg-white border border-border rounded-md p-6 flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
        <div className="flex items-center gap-6">
          <ProgressRing pct={p.progress.pct} live={p.status === "live"} />
          <div>
            <div className="flex items-center gap-2">
              {p.status === "live"
                ? <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F]"><CheckCircle2 className="h-3 w-3" />Live · Market-ready</span>
                : <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary text-muted-foreground"><Lock className="h-3 w-3" />Draft · Onboarding</span>}
            </div>
            <h1 className="font-head text-2xl lg:text-3xl font-extrabold tracking-tight mt-2">{p.title}</h1>
            <p className="text-muted-foreground text-sm">{p.address}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {p.property_type} · {p.bedrooms} bed · {p.bathrooms} bath
            </p>
            <div data-testid="property-owner-badge" className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F] text-sm font-semibold">
              <Users className="h-3.5 w-3.5" />Owner: {p.landlord_name}
            </div>
            {p.contractor_email && (
              <p className="text-xs text-muted-foreground mt-0.5">Contractor: {p.contractor_name || p.contractor_email} · {p.contractor_email}</p>
            )}
            {(p.amenities || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="property-amenities">
                {p.amenities.map((a) => <span key={a} className="text-[0.65rem] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{a}</span>)}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-stretch gap-2 w-full lg:w-auto">
          {canManage && (
            <Button variant="outline" size="sm" data-testid="edit-property-button" onClick={() => setEditOpen(true)}><Pencil className="h-3.5 w-3.5 mr-1.5" />Edit details</Button>
          )}
          {canManage && p.status !== "live" && (
            <Button
              data-testid="go-live-button"
              onClick={goLive}
              disabled={!complete || hasExpired}
              className="h-11"
            >
              {complete && !hasExpired ? <Rocket className="h-4 w-4 mr-1.5" /> : <Lock className="h-4 w-4 mr-1.5" />}
              {complete && !hasExpired ? "Publish to Market" : "Go Live locked"}
            </Button>
          )}
          {canManage && p.status === "live" && (
            <>
              <Button data-testid="feed-button" onClick={viewFeed} className="h-11"><Rss className="h-4 w-4 mr-1.5" />Generate Feed</Button>
              <Button data-testid="unpublish-button" variant="outline" onClick={unpublish}><Undo2 className="h-4 w-4 mr-1.5" />Unpublish</Button>
            </>
          )}
          {!complete && p.status !== "live" && (
            <p className="text-xs text-muted-foreground max-w-[180px] text-center flex items-center gap-1 justify-center">
              <ShieldCheck className="h-3 w-3" /> {p.progress.completed}/{p.progress.total} verified. Complete all to unlock.
            </p>
          )}
          {hasExpired && (
            <p className="text-xs text-[#9E382A] max-w-[180px] text-center">A compliance document is expired.</p>
          )}
        </div>
      </div>

      {["admin", "operations", "compliance"].includes(user.role) && (
        <ReadinessCard propertyId={p.id} rev={p} onJump={(t) => { setTab(t); setTimeout(() => document.querySelector(`[data-testid="tab-${t}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50); }} />
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="checklist" data-testid="tab-checklist">Checklist</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding">Onboarding Form</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({p.documents.length})</TabsTrigger>
          <TabsTrigger value="comms" data-testid="tab-comms">Communication</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">Issues ({(p.issues || []).length})</TabsTrigger>
          {["admin", "operations", "compliance", "landlord"].includes(user.role) && <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>}
          {["admin", "operations", "compliance", "landlord"].includes(user.role) && <TabsTrigger value="finances" data-testid="tab-finances">Finances</TabsTrigger>}
          {p.rental_type === "holiday" && ["admin", "operations", "compliance", "landlord"].includes(user.role) && <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>}
          {p.rental_type === "holiday" && ["admin", "operations", "compliance", "landlord"].includes(user.role) && <TabsTrigger value="cleaning" data-testid="tab-cleaning">Cleaning</TabsTrigger>}
          {["admin", "operations", "compliance"].includes(user.role) && <TabsTrigger value="legal" data-testid="tab-legal">Legal</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Trail</TabsTrigger>
          {p.status === "live" && <TabsTrigger value="syndication" data-testid="tab-syndication">Syndication</TabsTrigger>}
        </TabsList>

        {/* Checklist */}
        <TabsContent value="checklist" className="space-y-6 mt-5">
          {STAGES.map((stage) => {
            const items = p.checklist.filter((i) => i.stage === stage);
            const done = items.filter((i) => i.status === "complete").length;
            return (
              <div key={stage} className="bg-white border border-border rounded-md">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                  <span className="overline">{stage}</span>
                  <div className="flex items-center gap-3">
                    {isStaff && <button data-testid={`add-task-${stage.replace(/[^a-z]/gi, '')}`} onClick={() => addCustom(stage)} className="text-xs text-accent underline">+ Add task</button>}
                    <span className="text-xs font-mono text-muted-foreground">{done}/{items.length}</span>
                  </div>
                </div>
                <div className="divide-y divide-border">
                  {items.map((item) => {
                    const Icon = STATUS_ICON[item.status];
                    const doc = p.documents.find((d) => d.checklist_item_id === item.id);
                    const sig = p.signatures.find((s) => s.checklist_item_id === item.id);
                    const editable = canEditItem(item);
                    const mine = (item.assignees || []).includes(user.id);
                    return (
                      <motion.div layout key={item.id} data-testid={`checklist-item-${item.id}`} className={`px-5 py-4 flex items-start gap-3 ${mine ? "bg-[#C6A87C]/10 border-l-2 border-l-[#C6A87C]" : ""}`}>
                        <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${STATUS_COLOR[item.status]}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{item.name}</p>
                            <span className={`text-[0.6rem] px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[item.owner_role]}`}>{ROLE_LABELS[item.owner_role]}</span>
                            {item.is_custom && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#3a4a6b]/12 text-[#3a4a6b]">Custom</span>}
                            {mine && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#C6A87C]/30 text-[#8a6d3b] font-semibold">Assigned to you</span>}
                            {item.hidden_from_landlord && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Hidden from landlord</span>}
                            {item.requires_document && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground flex items-center gap-1"><Paperclip className="h-2.5 w-2.5" />Doc</span>}
                            {item.requires_signature && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground flex items-center gap-1"><FileSignature className="h-2.5 w-2.5" />Sign</span>}
                            {item.bides_supplies && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F]">Bides supplies</span>}
                            {(item.assignees || []).map((uid) => <span key={uid} className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#C6A87C]/20 text-[#8a6d3b]">{staffName(uid)}</span>)}
                            {(item.manual_assignees || []).map((nm) => <span key={nm} data-testid={`manual-assignee-${item.id}-${nm}`} className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#3a4a6b]/12 text-[#3a4a6b] inline-flex items-center gap-1">{nm}{isStaff && <button onClick={(e) => { e.stopPropagation(); removeManualAssignee(item, nm); }}><X className="h-2.5 w-2.5" /></button>}</span>)}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                          {doc && (
                            <p className="text-xs mt-1.5 flex items-center gap-1 text-[#2B4C3F]"><FileText className="h-3 w-3" />{doc.name}{doc.expiry_date ? ` · exp ${fmtDate(doc.expiry_date)}` : ""}</p>
                          )}
                          {sig && <p className="text-xs mt-1 flex items-center gap-1 text-[#2B4C3F]"><FileSignature className="h-3 w-3" />Signed by {sig.signer_name} · {fmtDate(sig.signed_at)}</p>}
                          {item.completed_by && item.status === "complete" && (
                            <p className="text-[0.65rem] text-muted-foreground mt-1">✓ Verified by {item.completed_by} · {fmtDate(item.completed_at)}</p>
                          )}
                          {item.status === "submitted" && (
                            <p className="text-[0.65rem] text-[#3a4a6b] mt-1" data-testid={`submitted-note-${item.id}`}>⏳ Submitted{item.submitted_by ? ` by ${item.submitted_by}` : ""} · awaiting staff review</p>
                          )}
                          {item.rejection_note && item.status !== "complete" && (
                            <p className="text-[0.65rem] text-[#9E382A] mt-1" data-testid={`rejection-note-${item.id}`}>↩ Returned: {item.rejection_note}</p>
                          )}
                        </div>
                        {editable && (
                          <div className="flex flex-col gap-2 items-end shrink-0">
                            {isStaff && (
                              <div className="flex items-center gap-1.5 flex-wrap justify-end">
                                <Select value="" onValueChange={(uid) => toggleAssignee(item, uid)}>
                                  <SelectTrigger data-testid={`assign-select-${item.id}`} className="h-7 w-36 text-[11px]"><span className="text-muted-foreground">+ Assign staff</span></SelectTrigger>
                                  <SelectContent>{staffList.map((s) => <SelectItem key={s.id} value={s.id}>{(item.assignees || []).includes(s.id) ? "✓ " : ""}{s.name}</SelectItem>)}</SelectContent>
                                </Select>
                                <ManualAssignPopover itemId={item.id} onAdd={(name) => addManualAssignee(item, name)} />
                                <Button size="sm" variant="ghost" className="h-7 text-[11px]" data-testid={`hide-toggle-${item.id}`} onClick={() => patchItem(item, { hidden_from_landlord: !item.hidden_from_landlord })}>{item.hidden_from_landlord ? "Show" : "Hide"}</Button>
                                {item.is_custom && <Button size="sm" variant="ghost" className="h-7 text-[11px] text-[#9E382A]" data-testid={`del-task-${item.id}`} onClick={() => delCustom(item)}>Delete</Button>}
                              </div>
                            )}
                            {item.requires_document && (
                              <Button size="sm" variant="outline" data-testid={`upload-btn-${item.id}`} onClick={() => setUploadItem(item)}><Upload className="h-3.5 w-3.5 mr-1" />Upload</Button>
                            )}
                            {item.requires_signature && !sig && (
                              <Button size="sm" variant="outline" data-testid={`sign-btn-${item.id}`} onClick={() => setSignItem(item)}><FileSignature className="h-3.5 w-3.5 mr-1" />Sign</Button>
                            )}
                            {isStaff && item.status === "submitted" && (
                              <div className="flex gap-1.5">
                                <Button size="sm" data-testid={`verify-btn-${item.id}`} onClick={() => setStatus(item, "complete")}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Verify</Button>
                                <Button size="sm" variant="outline" data-testid={`return-btn-${item.id}`} onClick={() => { const n = window.prompt("Reason for returning this task (optional):") || ""; setStatus(item, "pending", n); }}><Undo2 className="h-3.5 w-3.5 mr-1" />Return</Button>
                              </div>
                            )}
                            {isStaff ? (
                              <Select value={item.status} onValueChange={(v) => setStatus(item, v)}>
                                <SelectTrigger data-testid={`status-select-${item.id}`} className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="not_needed">Not Needed</SelectItem>
                                  <SelectItem value="pending">Required</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="submitted">Submitted</SelectItem>
                                  <SelectItem value="complete">Verified</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              item.status !== "complete" && (() => {
                                const missingDoc = item.requires_document && !doc;
                                const missingSig = item.requires_signature && !sig;
                                const blocked = missingDoc || missingSig;
                                return (
                                  <div className="flex flex-col items-end gap-1">
                                    <Button size="sm" data-testid={`submit-btn-${item.id}`} disabled={item.status === "submitted" || blocked} onClick={() => setStatus(item, "submitted")}>
                                      <Send className="h-3.5 w-3.5 mr-1" />{item.status === "submitted" ? "Submitted" : "Submit for review"}
                                    </Button>
                                    {blocked && <span className="text-[0.6rem] text-muted-foreground">{missingDoc ? "Upload a document first" : "Add a signature first"}</span>}
                                  </div>
                                );
                              })()
                            )}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </TabsContent>

        {/* Onboarding questionnaire */}
        <TabsContent value="onboarding" className="mt-5">
          <OnboardingTab p={p} role={user.role} reload={load} />
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents" className="mt-5">
          <DocumentsTab p={p} role={user.role} reload={load} onUpload={() => setUploadItem({ id: null, name: "General Document", requires_document: true, has_expiry: true })} />
        </TabsContent>

        {/* Comms */}
        <TabsContent value="comms" className="mt-5">
          <CommsTab p={p} reload={load} />
        </TabsContent>

        {/* Issues */}
        <TabsContent value="issues" className="mt-5">
          {p.rental_type === "holiday" && ["admin", "operations", "compliance"].includes(user.role) && <PublicReportCard propertyId={p.id} />}
          <IssuesTab p={p} role={user.role} reload={load} />
        </TabsContent>

        <TabsContent value="expenses" className="mt-5">
          {tab === "expenses" && <Expenses propertyId={p.id} />}
        </TabsContent>

        {/* Audit */}
        <TabsContent value="audit" className="mt-5">
          <AuditTab audit={audit} canUndo={["admin", "operations", "compliance"].includes(user.role)} reload={load} />
        </TabsContent>

        {/* Finances */}
        <TabsContent value="finances" className="mt-5">
          <FinanceTab property={p} role={user.role} />
        </TabsContent>

        {p.rental_type === "holiday" && (
          <TabsContent value="bookings" className="mt-5">
            <BookingsTab property={p} role={user.role} />
          </TabsContent>
        )}

        {p.rental_type === "holiday" && (
          <TabsContent value="cleaning" className="mt-5">
            <CleaningCard propertyId={p.id} role={user.role} />
          </TabsContent>
        )}

        {/* Legal */}
        {["admin", "operations", "compliance"].includes(user.role) && (
          <TabsContent value="legal" className="mt-5">
            <LegalTab property={p} role={user.role} />
          </TabsContent>
        )}

        {/* Syndication */}
        {p.status === "live" && (
          <TabsContent value="syndication" className="mt-5">
            <SyndicationTab p={p} pushes={pushes} canManage={canManage} onFeed={viewFeed} reload={load} />
          </TabsContent>
        )}
      </Tabs>

      {uploadItem && (
        <UploadDialog propertyId={id} item={uploadItem} onClose={() => setUploadItem(null)} onDone={() => { setUploadItem(null); load(); }} />
      )}
      {editOpen && (
        <EditPropertyDialog property={p} onClose={() => setEditOpen(false)} onDone={() => { setEditOpen(false); load(); }} />
      )}
      {signItem && (
        <Dialog open onOpenChange={() => setSignItem(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-head">Management Agreement — E-Signature</DialogTitle></DialogHeader>
            <div className="bg-secondary/40 border border-border rounded-md p-4 text-xs text-muted-foreground max-h-40 overflow-y-auto thin-scroll">
              <p className="font-medium text-foreground mb-1">Bides Property Management Agreement</p>
              <p>This agreement authorises Bides to manage the letting of <b>{p.title}</b> on behalf of the landlord {p.landlord_name}, at an agreed commission of {p.commission_pct}%. The landlord confirms ownership and grants marketing rights. By signing below you accept the full terms of management.</p>
            </div>
            <SignaturePad
              signerName={p.landlord_name}
              onSign={async (name, dataUrl) => {
                try {
                  await api.post("/signatures", { property_id: id, checklist_item_id: signItem.id, signer_name: name, signature_data: dataUrl });
                  toast.success("Agreement signed");
                  setSignItem(null);
                  load();
                } catch (e) { toast.error(apiError(e)); }
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function OnboardingTab({ p, role, reload }) {
  const canEdit = ["admin", "operations", "compliance", "landlord"].includes(role);
  const isStaffRole = ["admin", "operations", "compliance"].includes(role);
  const { brand } = usePublicSettings();
  const [intake, setIntake] = useState(p.intake || {});
  const [directors, setDirectors] = useState(p.directors || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/properties/${p.id}/intake`, { intake, directors });
      toast.success(data.synced?.length ? `Questionnaire saved · updated ${data.synced.join(", ")}` : "Onboarding questionnaire saved");
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/properties/${p.id}/onboarding.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `onboarding-${p.title || p.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const [sending, setSending] = useState(false);
  const [nudging, setNudging] = useState(false);
  const sendReminder = async () => {
    setNudging(true);
    try {
      const { data } = await api.post(`/properties/${p.id}/onboarding-reminder`);
      toast.success(data.delivered ? `Reminder emailed to ${data.sent_to}` : `Reminder logged (email not delivered) for ${data.sent_to}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setNudging(false); }
  };
  const sendLink = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/properties/${p.id}/send-onboarding`);
      if (data.delivered) {
        toast.success(`Onboarding link emailed to ${data.sent_to}`);
      } else {
        try { await navigator.clipboard.writeText(data.link.startsWith("http") ? data.link : `${window.location.origin}${data.link}`); } catch {}
        toast.success("Secure onboarding link copied to clipboard — email couldn't be delivered, share it directly.");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-border rounded-md p-5 space-y-6" data-testid="onboarding-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="overline text-accent">New Client Onboarding</p>
          <p className="text-xs text-muted-foreground mt-0.5">Property, access, ownership & director details.</p>
          {(() => {
            const S = {
              not_sent: { label: "Not sent", cls: "bg-secondary text-muted-foreground" },
              sent: { label: "Invited", cls: "bg-[#D98324]/15 text-[#D98324]" },
              opened: { label: "Opened by landlord", cls: "bg-[#3a4a6b]/12 text-[#3a4a6b]" },
              completed: { label: "Completed by landlord", cls: "bg-[#2B4C3F]/12 text-[#2B4C3F]" },
            }[p.onboarding_status || "not_sent"];
            return <span data-testid="onboarding-status-badge" className={`inline-block mt-2 text-[0.65rem] px-2 py-0.5 rounded-full ${S.cls}`}>{S.label}</span>;
          })()}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isStaffRole && <Button variant="outline" data-testid="send-onboarding-link" onClick={sendLink} disabled={sending}><Mail className="h-4 w-4 mr-1" />{sending ? "Sending…" : "Email to landlord"}</Button>}
          {isStaffRole && ["sent", "opened"].includes(p.onboarding_status) && <Button variant="outline" data-testid="send-reminder-now" onClick={sendReminder} disabled={nudging}><BellRing className="h-4 w-4 mr-1" />{nudging ? "Sending…" : "Send reminder now"}</Button>}
          <Button variant="outline" data-testid="export-onboarding-pdf" onClick={exportPdf}><Download className="h-4 w-4 mr-1" />Export PDF</Button>
          {canEdit && <Button data-testid="save-onboarding-button" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save questionnaire"}</Button>}
        </div>
      </div>
      {isStaffRole && (p.onboarding_reminders_count || p.onboarding_status === "sent" || p.onboarding_status === "opened") && p.onboarding_status !== "completed" && (() => {
        const count = p.onboarding_reminders_count || 0;
        const max = brand?.onboarding_reminder_max ?? 3;
        const days = brand?.onboarding_reminder_days ?? 7;
        const last = p.onboarding_last_reminder_at || p.onboarding_sent_at;
        let nextTxt = "—";
        if (count >= max) nextTxt = "reminder cap reached";
        else if (last) { const nd = new Date(new Date(last).getTime() + days * 86400000); nextTxt = `around ${fmtDate(nd.toISOString())}`; }
        return (
          <div data-testid="reminder-log" className="text-xs text-muted-foreground border border-border rounded-md px-3 py-2 bg-secondary/30">
            Auto-reminders: <strong className="text-foreground">{count}/{max}</strong> sent{p.onboarding_last_reminder_at ? ` · last ${fmtDate(p.onboarding_last_reminder_at)}` : ""} · next {nextTxt}
          </div>
        );
      })()}
      {canEdit ? (
        <OnboardingFields intake={intake} setIntake={setIntake} directors={directors} setDirectors={setDirectors} mode="staff" />
      ) : (
        <p className="text-sm text-muted-foreground">You do not have permission to edit this form.</p>
      )}
    </div>
  );
}

function UploadDialog({ propertyId, item, onClose, onDone }) {
  const [name, setName] = useState(item.name);
  const [expiry, setExpiry] = useState("");
  const [fileData, setFileData] = useState("");
  const [fileName, setFileName] = useState("");

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setFileData(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    try {
      await api.post("/documents", {
        property_id: propertyId,
        doc_type: item.name,
        name: fileName || name,
        file_data: fileData,
        expiry_date: expiry || null,
        checklist_item_id: item.id,
      });
      toast.success("Document uploaded");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-head">Upload — {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline">File</Label>
            <Input data-testid="doc-file-input" type="file" onChange={onFile} className="mt-1.5" />
          </div>
          <div>
            <Label className="overline">Document label</Label>
            <Input data-testid="doc-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
          </div>
          {item.has_expiry && (
            <div>
              <Label className="overline">Valid until</Label>
              <Input data-testid="doc-expiry-input" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1.5" />
              <p className="text-xs text-muted-foreground mt-1">Reminders trigger 30/15/7 days before expiry.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="save-doc-button" onClick={submit}>Save Document</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentsTab({ p, onUpload, reload, role }) {
  const canRenew = ["admin", "operations", "compliance"].includes(role);
  const [showArchived, setShowArchived] = useState(false);
  const archivedCount = p.documents.filter((d) => d.archived).length;
  const docs = p.documents.filter((d) => showArchived || !d.archived);

  const [archiving, setArchiving] = useState(null);
  const [reason, setReason] = useState("");
  const setArchived = async (d, archived, why = "") => {
    try {
      await api.patch(`/documents/${d.id}/archive`, { archived, reason: why });
      toast.success(archived ? "Document archived" : "Document restored");
      setArchiving(null); setReason("");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleArchive = (d) => { if (d.archived) setArchived(d, false); else { setReason(""); setArchiving(d); } };

  const openDoc = async (d, download) => {
    try { await openPdf(`/documents/${d.id}/file`, download, d.name); }
    catch { toast.error("Could not open document"); }
  };

  const renew = async (d) => {
    try {
      const { data } = await api.post(`/documents/${d.id}/request-renewal`);
      toast.success(`Renewal emailed to ${data.sent_to.join(", ")}`);
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const verify = async (d) => {
    try { await api.post(`/documents/${d.id}/verify`); toast.success("Document verified"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-end gap-4 mb-4 flex-wrap">
        {archivedCount > 0 && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <Switch data-testid="show-archived-docs-switch" checked={showArchived} onCheckedChange={setShowArchived} />
            Show archived ({archivedCount})
          </label>
        )}
        <Button variant="outline" data-testid="add-doc-button" onClick={onUpload}><Upload className="h-4 w-4 mr-1.5" />Add Document</Button>
      </div>
      {docs.length === 0 ? (
        <p data-testid="docs-empty" className="p-10 text-center text-sm text-muted-foreground bg-white border border-border rounded-md">{archivedCount ? "All documents are archived." : "No documents in the vault yet."}</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d) => {
            const b = expiryBadge(d.expiry_status || "valid");
            const flagged = !d.archived && (d.expiry_status === "expiring" || d.expiry_status === "expired");
            return (
              <div key={d.id} data-testid={`doc-card-${d.id}`} className={`bg-white border border-border rounded-md p-4 flex flex-col ${d.archived ? "opacity-60" : ""}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="h-9 w-9 rounded-md bg-secondary flex items-center justify-center"><FileText className="h-4 w-4" /></div>
                  {d.archived && <span data-testid={`doc-archived-badge-${d.id}`} className="text-[0.65rem] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground ml-auto">Archived</span>}
                  {d.expiry_date && !d.archived && <span className={`text-[0.65rem] px-2 py-0.5 rounded-full border ${b.cls}`}>{b.label}</span>}
                </div>
                <p className="font-medium text-sm mt-3 truncate">{d.doc_type}</p>
                <p className="text-xs text-muted-foreground truncate">{d.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  {d.status === "pending"
                    ? <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#D98324]/12 text-[#D98324]">Pending verification</span>
                    : <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F]">Verified</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Uploaded {fmtDate(d.uploaded_at)} by {d.uploaded_by}</p>
                {d.expiry_date && <p className="text-xs mt-0.5">Expires {fmtDate(d.expiry_date)}</p>}
                {d.archived && (
                  <div data-testid={`doc-archive-note-${d.id}`} className="mt-2 text-xs bg-secondary/60 border-l-2 border-[#C6A87C] rounded-sm px-2 py-1.5">
                    <p className="italic">{d.archive_reason ? `"${d.archive_reason}"` : "No reason given"}</p>
                    {d.archived_by && <p className="text-[0.65rem] text-muted-foreground mt-0.5">Archived by {d.archived_by}{d.archived_at ? ` · ${fmtDate(d.archived_at)}` : ""}</p>}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                  <Button size="sm" variant="outline" className="flex-1" data-testid={`doc-view-${d.id}`} onClick={() => openDoc(d, false)}><Eye className="h-3.5 w-3.5 mr-1" />View</Button>
                  <Button size="sm" variant="outline" className="flex-1" data-testid={`doc-download-${d.id}`} onClick={() => openDoc(d, true)}><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
                </div>
                {d.status === "pending" && canRenew && (
                  <Button size="sm" className="mt-2 w-full" data-testid={`doc-verify-${d.id}`} onClick={() => verify(d)}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Verify Document</Button>
                )}
                {flagged && canRenew && (
                  <Button size="sm" variant="outline" className="mt-2 w-full" data-testid={`doc-renew-${d.id}`} onClick={() => renew(d)}><Send className="h-3.5 w-3.5 mr-1" />Request Renewal</Button>
                )}
                {canRenew && (
                  <Button size="sm" variant="ghost" className="mt-2 w-full text-muted-foreground" data-testid={`doc-archive-${d.id}`} onClick={() => toggleArchive(d)}>
                    {d.archived ? <><ArchiveRestore className="h-3.5 w-3.5 mr-1" />Restore</> : <><Archive className="h-3.5 w-3.5 mr-1" />Archive</>}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={!!archiving} onOpenChange={(v) => { if (!v) setArchiving(null); }}>
        <DialogContent className="max-w-md" data-testid="archive-doc-dialog">
          <DialogHeader><DialogTitle className="font-head">Archive {archiving?.doc_type}</DialogTitle></DialogHeader>
          <div>
            <Label className="overline">Quick reasons · one click archives</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5 mb-3">
              {["Superseded", "Expired, renewed", "Duplicate", "Uploaded in error", "No longer required"].map((r) => (
                <button key={r} type="button" data-testid={`archive-quick-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setArchived(archiving, true, r)}
                  className="text-xs px-2.5 py-1 rounded-full border border-border bg-white hover:border-[#C6A87C] hover:bg-[#C6A87C]/10 transition-colors">
                  {r}
                </button>
              ))}
            </div>
            <Label className="overline">Or write your own (optional)</Label>
            <Textarea data-testid="archive-reason-input" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Superseded by 2026 certificate" className="mt-1.5" />
            <p className="text-[0.65rem] text-muted-foreground mt-1 text-right">{reason.length}/300</p>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="archive-cancel-btn" onClick={() => setArchiving(null)}>Cancel</Button>
            <Button data-testid="archive-confirm-btn" onClick={() => setArchived(archiving, true, reason.trim())}><Archive className="h-4 w-4 mr-1" />Archive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AuditTab({ audit, canUndo, reload }) {
  const [undoing, setUndoing] = useState(null);
  const undo = async (a) => {
    setUndoing(a.id);
    try {
      const { data } = await api.post(`/audit/${a.id}/undo`);
      toast.success(`Restored ${data.reverted.join(", ")}` + (data.skipped.length ? ` · skipped ${data.skipped.join(", ")} (changed since)` : ""));
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUndoing(null); }
  };
  const ICONS = {
    property_created: FileText, task_completed: CheckCircle2, task_updated: Clock,
    document_uploaded: Upload, signature_captured: FileSignature, went_live: Rocket,
    unpublished: Undo2, renewal_requested: Send, portal_push: Rss, property_updated: Pencil, onboarding_synced: RefreshCw, onboarding_sync_undone: Undo2,
  };
  if (!audit.length) return <p className="p-10 text-center text-sm text-muted-foreground bg-white border border-border rounded-md">No activity recorded yet.</p>;
  return (
    <div className="relative pl-6 space-y-3 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-px before:bg-border">
      {audit.map((a) => {
        const Icon = ICONS[a.action] || History;
        return (
          <div key={a.id} data-testid={`audit-${a.id}`} className="relative">
            <span className="absolute -left-[1.35rem] top-1.5 h-4 w-4 rounded-full bg-white border border-border flex items-center justify-center"><Icon className="h-2.5 w-2.5" /></span>
            <div className="bg-white border border-border rounded-md p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{a.detail}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(a.created_at)}</span>
              </div>
              <p className="text-[0.65rem] text-muted-foreground mt-1 uppercase tracking-wider">{a.actor_name} · {a.actor_role}</p>
              {a.changes?.length > 0 && (
                <div data-testid={`audit-changes-${a.id}`} className="mt-2 border-t border-border pt-2 space-y-1">
                  {a.changes.map((c, i) => (
                    <div key={i} data-testid={`audit-change-${a.id}-${i}`} className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-x-3 text-xs">
                      <span className="text-muted-foreground capitalize">{c.field}</span>
                      <span className="break-words">
                        <span className="line-through text-[#9E382A]/80">{c.old || "(empty)"}</span>
                        <span className="mx-1.5 text-muted-foreground">→</span>
                        <span className="font-medium text-[#2B4C3F]">{c.new || "(empty)"}</span>
                      </span>
                    </div>
                  ))}
                  {a.action === "onboarding_synced" && (a.undone_at ? (
                    <p data-testid={`audit-undone-${a.id}`} className="text-[0.65rem] text-muted-foreground pt-1">Undone by {a.undone_by} · {fmtDate(a.undone_at)}</p>
                  ) : canUndo && a.changes.some((c) => c.target) && (
                    <Button size="sm" variant="outline" className="mt-1.5 h-7" data-testid={`audit-undo-${a.id}`} disabled={undoing === a.id} onClick={() => undo(a)}>
                      <Undo2 className="h-3.5 w-3.5 mr-1" />{undoing === a.id ? "Undoing…" : "Undo these changes"}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SyndicationTab({ p, pushes, canManage, onFeed, reload }) {
  const rePush = async () => {
    try { const { data } = await api.post(`/properties/${p.id}/push-portals`); toast.success(`Pushed to ${data.pushed.join(", ") || "no portals"}`); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-white border border-border rounded-md p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Market-ready · Syndication</p>
          <p className="text-sm text-muted-foreground mt-1">Listing feed generated on go-live. Portal pushes are simulated until live datafeed credentials are added.</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" data-testid="view-feed-button" onClick={onFeed}><Rss className="h-4 w-4 mr-1.5" />View Feed</Button>
            <Button data-testid="repush-button" onClick={rePush}><RefreshCw className="h-4 w-4 mr-1.5" />Re-push</Button>
          </div>
        )}
      </div>
      {pushes.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground bg-white border border-border rounded-md">No portal pushes yet.</p>
      ) : (
        <div className="bg-white border border-border rounded-md divide-y divide-border">
          {pushes.map((pu) => (
            <div key={pu.id} data-testid={`push-${pu.id}`} className="px-5 py-3 flex items-center gap-4">
              <div className="h-9 w-9 rounded-md bg-[#2B4C3F]/12 text-[#2B4C3F] flex items-center justify-center"><ExternalLink className="h-4 w-4" /></div>
              <div className="flex-1">
                <p className="font-medium text-sm">{pu.portal}</p>
                <p className="text-xs text-muted-foreground font-mono">Ref {pu.reference}</p>
              </div>
              <div className="text-right">
                <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-[#C6A87C]/20 text-[#8a6d3b]">Simulated</span>
                <p className="text-xs text-muted-foreground mt-1">{fmtDate(pu.pushed_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditPropertyDialog({ property, onClose, onDone }) {
  const [amenityList, setAmenityList] = useState([]);
  const [form, setForm] = useState({
    title: property.title, address: property.address,
    council_tax_band: property.council_tax_band || "", commission_pct: property.commission_pct ?? "",
    monthly_rent: property.monthly_rent || 0,
    asking_price: property.asking_price || 0,
    description: property.description || "", contractor_name: property.contractor_name || "",
    contractor_email: property.contractor_email || "", amenities: property.amenities || [],
  });
  useEffect(() => { api.get("/settings").then((r) => setAmenityList(r.data.amenities || [])).catch(() => {}); }, []);
  const toggle = (a) => setForm((f) => ({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] }));
  const submit = async () => {
    try { await api.put(`/properties/${property.id}`, { ...form, commission_pct: form.commission_pct === "" ? null : Number(form.commission_pct), monthly_rent: Number(form.monthly_rent), asking_price: Number(form.asking_price) }); toast.success("Property updated"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-head">Edit Property</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto thin-scroll pr-1">
          <div><Label className="overline">Title</Label><Input data-testid="edit-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="overline">Council Tax</Label><Input value={form.council_tax_band} onChange={(e) => setForm({ ...form, council_tax_band: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Commission %</Label><Input data-testid="edit-commission-input" type="number" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} className="mt-1.5" placeholder="Default" /></div>
            <div><Label className="overline">Rent £/mo</Label><Input data-testid="edit-rent-input" type="number" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label className="overline">Asking price £ (for investor matching)</Label><Input data-testid="edit-asking-input" type="number" value={form.asking_price} onChange={(e) => setForm({ ...form, asking_price: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Contractor name</Label><Input data-testid="edit-contractor-name" value={form.contractor_name} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Contractor email</Label><Input data-testid="edit-contractor-email" value={form.contractor_email} onChange={(e) => setForm({ ...form, contractor_email: e.target.value })} className="mt-1.5" /></div>
          </div>
          {amenityList.length > 0 && (
            <div>
              <Label className="overline">Amenities</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5" data-testid="edit-amenities">
                {amenityList.map((a) => (
                  <label key={a} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" data-testid={`amenity-check-${a}`} checked={form.amenities.includes(a)} onChange={() => toggle(a)} className="accent-[#18181A]" />{a}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div><Label className="overline">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button data-testid="save-edit-property" onClick={submit}>Save Changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CommsTab({ p, reload }) {
  const [channel, setChannel] = useState("note");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const icons = { email: Mail, sms: MessageSquare, note: StickyNote };

  const send = async () => {
    if (!body.trim()) return;
    try {
      await api.post("/communications", { property_id: p.id, channel, subject, body });
      setBody(""); setSubject("");
      toast.success("Logged to property file");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 bg-white border border-border rounded-md p-4 h-fit">
        <span className="overline">Log a message</span>
        <div className="mt-3 space-y-3">
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger data-testid="comm-channel-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="note">Internal Note</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
            </SelectContent>
          </Select>
          {channel === "email" && <Input data-testid="comm-subject-input" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />}
          <Textarea data-testid="comm-body-input" placeholder="Message…" value={body} onChange={(e) => setBody(e.target.value)} rows={4} />
          <Button data-testid="comm-send-button" onClick={send} className="w-full"><Send className="h-4 w-4 mr-1.5" />Log</Button>
        </div>
      </div>
      <div className="lg:col-span-2">
        {p.communications.length === 0 ? (
          <p className="p-10 text-center text-sm text-muted-foreground bg-white border border-border rounded-md">No communications logged.</p>
        ) : (
          <div className="relative pl-6 space-y-4 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-px before:bg-border">
            {p.communications.map((c) => {
              const Icon = icons[c.channel] || StickyNote;
              return (
                <div key={c.id} data-testid={`comm-${c.id}`} className="relative">
                  <span className="absolute -left-[1.35rem] top-1 h-4 w-4 rounded-full bg-white border border-border flex items-center justify-center"><Icon className="h-2.5 w-2.5" /></span>
                  <div className="bg-white border border-border rounded-md p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium capitalize">{c.channel}{c.subject ? ` · ${c.subject}` : ""}</span>
                      <span className="text-xs text-muted-foreground">{fmtDate(c.created_at)}</span>
                    </div>
                    <p className="text-sm mt-1">{c.body}</p>
                    <p className="text-[0.65rem] text-muted-foreground mt-1">{c.created_by} · {c.created_by_role}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


const ISSUE_STATUS = {
  new: { label: "New", cls: "bg-[#3a4a6b]/12 text-[#3a4a6b]" },
  in_progress: { label: "In Progress", cls: "bg-[#D98324]/12 text-[#D98324]" },
  resolved: { label: "Resolved", cls: "bg-[#2B4C3F]/12 text-[#2B4C3F]" },
};

function ContractorAssignInline({ issue, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contractor_name: "", contractor_email: "", note: "" });
  const submit = async () => {
    try {
      const { data } = await api.post(`/issues/${issue.id}/assign-contractor`, form);
      toast.success(data.emailed ? `Job emailed to ${form.contractor_email}` : "Assigned (email not sent)");
      setOpen(false);
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="w-full" data-testid={`prop-assign-contractor-${issue.id}`}><Wrench className="h-3.5 w-3.5 mr-1" />Contractor</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-head">Assign Contractor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Emails the job details for <strong>{issue.title}</strong> and marks it In Progress.</p>
          <ContractorPicker onPick={(c) => setForm({ ...form, contractor_name: c.name, contractor_email: c.email })} />
          <div><Label className="overline">Contractor name</Label><Input data-testid="prop-contractor-name-input" value={form.contractor_name} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Contractor email</Label><Input data-testid="prop-contractor-email-input" value={form.contractor_email} onChange={(e) => setForm({ ...form, contractor_email: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Note (optional)</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button data-testid="prop-send-contractor-button" onClick={submit} disabled={!form.contractor_name || !form.contractor_email}>Assign & Email</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function IssuesTab({ p, role, reload }) {
  const staff = ["admin", "operations", "compliance"].includes(role);
  const canDismiss = ["admin", "operations"].includes(role);
  const issues = p.issues || [];

  const setStatus = async (iss, status) => {
    try { await api.patch(`/issues/${iss.id}`, { status }); toast.success("Updated"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const dismiss = async (iss) => {
    if (!window.confirm(`Dismiss "${iss.title}"? This removes the report permanently.`)) return;
    try { await api.delete(`/issues/${iss.id}`); toast.success("Issue dismissed"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (issues.length === 0)
    return <p className="p-10 text-center text-sm text-muted-foreground bg-white border border-border rounded-md">No repair issues reported for this property.</p>;

  return (
    <div className="space-y-3">
      {issues.map((iss) => {
        const sm = ISSUE_STATUS[iss.status];
        return (
          <div key={iss.id} data-testid={`prop-issue-${iss.id}`} className="bg-white border border-border rounded-md p-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-secondary flex items-center justify-center shrink-0"><Wrench className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium">{iss.title}</p>
                  <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{iss.category}</span>
                  <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{iss.priority}</span>
                  {iss.source === "public" && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#D98324]/15 text-[#D98324]">Guest report</span>}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{iss.description}</p>
                {iss.photo && <img src={fileUrl(iss.photo)} alt="issue" data-testid={`prop-issue-photo-${iss.id}`} className="mt-2 h-28 rounded-md border border-border object-cover" />}
                <IssueExtras issue={iss} role={role} reload={reload} />
                <p className="text-xs text-muted-foreground mt-2">Raised by {iss.raised_by_name} · {fmtDate(iss.created_at)}{iss.assigned_to ? ` · Assigned: ${iss.assigned_to}` : ""}</p>
                {iss.updates?.length > 0 && (
                  <div className="mt-2 pl-3 border-l-2 border-border space-y-1">
                    {iss.updates.slice(-4).map((u, i) => <p key={i} className="text-xs text-muted-foreground">{u.text} — {u.by}</p>)}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0 w-36">
                <span className={`text-xs px-2 py-1 rounded-full ${sm.cls}`}>{sm.label}</span>
                {staff && (
                  <Select value={iss.status} onValueChange={(v) => setStatus(iss, v)}>
                    <SelectTrigger data-testid={`prop-issue-status-${iss.id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {staff && <ContractorAssignInline issue={iss} reload={reload} />}
                {canDismiss && <button data-testid={`prop-issue-dismiss-${iss.id}`} onClick={() => dismiss(iss)} className="text-xs text-[#9E382A] underline">Dismiss</button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

